import * as React from 'react';
import { FunctionComponent, useEffect, useState } from 'react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTag } from '@fortawesome/free-solid-svg-icons';
import { getFullName } from 'Src/helpers/autocomplete/autocomplete';
import { getUserTagColor, USER_TAG_DEFAULT_COLOR, USER_TAG_PALETTE } from 'Src/helpers/tags/tagColors';
import './UserTagActionModal.scss';
import { UnsavedChangesModal } from '../UnsavedChangesModal/UnsavedChangesModal';

interface Props {
  modalTitle: string;
  userTags: any[];
  selectedUserTagId: string;
  selectedUserTagIds?: string[];
  selectedStudents?: any[];
  excludedUserTagIds?: string[];
  onUserTagChange: (event) => void;
  onUserTagIdsChange?: (selectedTagIds: string[]) => void;
  onCreateTag?: (tagName: string, color: string) => Promise<any>;
  onCloseClick: () => void;
  isOpen: boolean;
  onSaveClick: (selectedTagIds?: string[]) => void;
}

const normalizeName = (name: string): string =>
  String(name || '')
    .trim()
    .replace(/\s+/g, ' ')
    .slice(0, 40);

export const UserTagAssignModal: FunctionComponent<Props> = props => {
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState(USER_TAG_DEFAULT_COLOR);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [isDiscardWarningOpen, setIsDiscardWarningOpen] = useState(false);
  const [localSelectedIds, setLocalSelectedIds] = useState<string[]>([]);

  useEffect(() => {
    if (props.isOpen) {
      setIsDiscardWarningOpen(false);
      setLocalSelectedIds(props.selectedUserTagIds || (props.selectedUserTagId ? [props.selectedUserTagId] : []));
    }
  }, [props.isOpen]);

  if (!props.isOpen) {
    return null;
  }

  const modalClasses = classNames('userTagActionModal', {
    mDisplayBlock: props.isOpen,
    show: props.isOpen,
    modal: true,
    fade: true
  });

  const selectedIds = props.onUserTagIdsChange
    ? props.selectedUserTagIds || (props.selectedUserTagId ? [props.selectedUserTagId] : [])
    : localSelectedIds;
  const selectedStudents = props.selectedStudents || [];
  const excludedIds = props.excludedUserTagIds || [];
  const selectedTags = props.userTags.filter(tag => selectedIds.includes(tag.id));
  const availableTags = props.userTags.filter(
    tag => excludedIds.indexOf(tag.id) === -1 && !selectedIds.includes(tag.id)
  );
  const normalizedNewName = normalizeName(newTagName);
  const isDuplicate = Boolean(
    normalizedNewName &&
      props.userTags.some(tag => String(tag.tagName).toLowerCase() === normalizedNewName.toLowerCase())
  );
  const isDirty = selectedIds.length > 0 || Boolean(newTagName.trim()) || newTagColor !== USER_TAG_DEFAULT_COLOR;
  const requestClose = () => (isDirty ? setIsDiscardWarningOpen(true) : props.onCloseClick());
  const discardChanges = () => {
    setNewTagName('');
    setNewTagColor(USER_TAG_DEFAULT_COLOR);
    setCreateError('');
    setIsDiscardWarningOpen(false);
    props.onCloseClick();
  };

  const toggleTag = (tagId: string) => {
    const nextIds = selectedIds.includes(tagId) ? selectedIds.filter(id => id !== tagId) : [...selectedIds, tagId];

    if (props.onUserTagIdsChange) {
      props.onUserTagIdsChange(nextIds);
    } else {
      setLocalSelectedIds(nextIds);
    }
  };

  const createTag = () => {
    if (!props.onCreateTag || !normalizedNewName || isDuplicate || isCreating) return;

    setIsCreating(true);
    setCreateError('');
    props
      .onCreateTag(normalizedNewName, newTagColor)
      .then(createdTag => {
        const createdId = createdTag && createdTag.id;
        setNewTagName('');
        setNewTagColor(USER_TAG_DEFAULT_COLOR);
        if (createdId) {
          if (props.onUserTagIdsChange) {
            props.onUserTagIdsChange([...selectedIds, createdId]);
          } else {
            setLocalSelectedIds([...selectedIds, createdId]);
          }
        }
      })
      .catch(error => {
        console.error(error);
        setCreateError('Unable to create tag.');
      })
      .then(() => setIsCreating(false));
  };

  return (
    <div className={modalClasses} onMouseDown={event => event.target === event.currentTarget && requestClose()}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Assign tags</h5>
            <button type="button" className="close" onClick={requestClose}>
              <span>&times;</span>
            </button>
          </div>
          <div className="modal-body">
            <div className="userTagAction">
              <div className="userTagAction__intro">
                <p>Tags help you group and filter students.</p>
              </div>

              <div className="userTagAction__students">
                <div className="userTagAction__avatars" aria-hidden="true">
                  {selectedStudents.slice(0, 4).map(student => (
                    <span key={student.id} className="userTagAction__avatar">
                      {`${String(student.firstName || '?').charAt(0)}${String(student.lastName || '').charAt(0)}`}
                    </span>
                  ))}
                </div>
                <span>
                  Tagging {selectedStudents.length} {selectedStudents.length === 1 ? 'student' : 'students'}.
                </span>
              </div>

              {selectedTags.length > 0 && (
                <div className="userTagAction__selection">
                  <span className="userTagAction__label">Selected tags</span>
                  <div className="userTagAction__chips userTagAction__chips--selected">
                    {selectedTags.map(tag => {
                      const color = getUserTagColor(tag);
                      return (
                        <button
                          key={tag.id}
                          type="button"
                          className="userTagAction__chip"
                          data-selected="true"
                          style={{ color: '#fff', backgroundColor: color }}
                          onClick={() => toggleTag(tag.id)}
                        >
                          <span className="userTagAction__dot" />
                          {tag.tagName}
                          <span aria-hidden="true">×</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div>
                <span className="userTagAction__label">Available tags</span>
                {availableTags.length ? (
                  <div className="userTagAction__chips">
                    {availableTags.map(tag => {
                      const color = getUserTagColor(tag);
                      return (
                        <button
                          key={tag.id}
                          type="button"
                          className="userTagAction__chip"
                          data-selected="false"
                          style={{ color, backgroundColor: `${color}1A` }}
                          onClick={() => toggleTag(tag.id)}
                        >
                          <span className="userTagAction__dot" />
                          {tag.tagName}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="userTagAction__empty">
                    {props.userTags.length
                      ? 'All available tags are already assigned.'
                      : 'No tags have been created yet.'}
                  </div>
                )}
              </div>

              <div className="userTagAction__create">
                <span className="userTagAction__label">Or create a new tag</span>
                <div className="userTagAction__createGrid">
                  <input
                    value={newTagName}
                    maxLength={40}
                    placeholder="New tag name"
                    onChange={event => setNewTagName(event.target.value)}
                    onKeyDown={event => {
                      if (event.key === 'Enter') createTag();
                    }}
                  />
                  <div className="userTagAction__swatches">
                    {USER_TAG_PALETTE.map(color => (
                      <button
                        key={color}
                        type="button"
                        className="userTagAction__swatch"
                        data-selected={newTagColor === color}
                        style={{ backgroundColor: color }}
                        aria-label={`Choose ${color}`}
                        onClick={() => setNewTagColor(color)}
                      />
                    ))}
                  </div>
                  <button
                    type="button"
                    className="sit-btn sit-btn--secondary"
                    disabled={!normalizedNewName || isDuplicate || isCreating || !props.onCreateTag}
                    onClick={createTag}
                  >
                    <FontAwesomeIcon icon={faPlus} /> Create
                  </button>
                </div>
                {isDuplicate ? <em className="userTagAction__error">That tag already exists.</em> : null}
                {createError ? <em className="userTagAction__error">{createError}</em> : null}
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="sit-btn sit-btn--ghost" onClick={requestClose}>
              Cancel
            </button>
            <button
              type="button"
              className="sit-btn sit-btn--primary"
              disabled={selectedIds.length === 0}
              onClick={() => props.onSaveClick(selectedIds)}
            >
              <FontAwesomeIcon icon={faTag} /> Assign tags
            </button>
          </div>
        </div>
      </div>
      <UnsavedChangesModal
        isOpen={isDiscardWarningOpen}
        message="Your tag selection has not been saved. If you close now, those changes will be lost."
        onKeepEditing={() => setIsDiscardWarningOpen(false)}
        onDiscard={discardChanges}
      />
    </div>
  );
};

UserTagAssignModal.displayName = 'UserTagAssignModal';
